import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import confetti from "canvas-confetti";
import {
  Check,
  ChefHat,
  MessageCircle,
  NotebookText,
  Pause,
  Play,
  RotateCcw,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Eyebrow } from "@/components/editorial";
import { useCookingSession, type CookingMode } from "@/hooks/useCookingSession";
import { useWakeLock } from "@/hooks/useWakeLock";
import { formatTimer, useCookingTimer } from "@/hooks/useCookingTimer";
import { useHaptics } from "@/hooks/useHaptics";
import { cn } from "@/lib/utils";
import { CookingChatSheet } from "@/features/cooking/CookingChatSheet";
import type { CookingStep } from "@/types/menu";
import { resolveAssetKey } from "@/features/cooking/assetResolver";
import {
  ChopIcon,
  StirIcon,
  BoilIcon,
  RoastIcon,
  PeelIcon,
  SeasonIcon,
  RestIcon,
  OvenIcon,
  TimerIcon,
} from "@/components/cooking-icons";
import type { CookingIconName } from "@/components/cooking-icons";

/**
 * Cooking Mode — BRD §3.4 crown jewel.
 *
 * Story-style: one screen = one step. Tap zones navigate (left third = back,
 * right third = next, center = play/pause timer). Wake-lock engaged for the
 * whole session. Haptics on every transition. Progress + step index persist
 * via Capacitor Preferences (resumes the user where they left off).
 *
 * Phase 10: mode-aware via `?recipe=<slug>` query param.
 *   - No query  → batch mode (Sunday plan).
 *   - With slug → per-recipe mode (lazily fetches `prep_steps`).
 * A segmented control in the header lets the user switch between the
 * two without leaving the screen.
 */
export function CookingPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const recipeSlug = params.get("recipe");
  const mode: CookingMode = recipeSlug ? "recipe" : "batch";

  const session = useCookingSession(mode, recipeSlug);
  const step: CookingStep | undefined = session.step;
  const isDone = step ? session.doneOrders.includes(step.order) : false;
  const total = session.steps.length;
  const progress = total > 0 ? ((session.index + 1) / total) * 100 : 0;

  useWakeLock(true);
  const haptics = useHaptics();

  // Timers are keyed by step identity: a running timer keeps counting (and
  // its native alarm stays scheduled) while the user looks at another step.
  const timerKey = step ? `${mode}:${recipeSlug ?? ""}:${step.order}` : null;
  const timer = useCookingTimer(step?.timer_seconds ?? null, timerKey);

  const [chatOpen, setChatOpen] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    if (timer.ringing) haptics.alert();
  }, [timer.ringing, haptics]);

  // Loading: menu still arriving (always) or recipe steps still generating.
  if (session.isLoadingPlan || !session.plan) {
    return <LoadingScreen />;
  }
  if (mode === "recipe" && !session.recipe) {
    return (
      <FallbackScreen
        title="Recette introuvable"
        body="Cette recette ne fait pas partie de votre menu actuel."
      />
    );
  }
  if (mode === "recipe" && session.isLoadingSteps) {
    return (
      <LoadingScreen
        title={session.title}
        sub="La brigade prépare les étapes…"
      />
    );
  }
  if (mode === "recipe" && session.stepsError && total === 0) {
    return (
      <FallbackScreen title="Étapes indisponibles" body={session.stepsError} />
    );
  }
  if (total === 0 || !step) {
    return (
      <FallbackScreen
        title={
          mode === "recipe"
            ? "Aucune étape pour cette recette."
            : "Aucun plan de batch cooking pour cette semaine."
        }
      />
    );
  }

  function handleTimerToggle() {
    if (step?.timer_seconds == null) return;
    haptics.tick();
    if (timer.running) timer.pause();
    else timer.start();
  }
  function handleNext() {
    haptics.step();
    session.next();
  }
  function handlePrev() {
    haptics.tick();
    session.prev();
  }
  function handleComplete() {
    if (!step) return;
    haptics.complete();
    if (isDone) {
      session.markUndone(step.order);
    } else {
      session.markDone(step.order);
      if (mode === "batch" && session.index === total - 1) {
        setShowCelebration(true);
      } else if (session.index < total - 1) {
        session.next();
      }
    }
  }

  function switchMode(next: CookingMode) {
    if (next === mode) return;
    haptics.tick();
    if (next === "batch") {
      // Drop the query param.
      setParams({});
      return;
    }
    // Switching to recipe mode: pick the first day's dinner as a sensible default.
    const firstDinner = session.plan?.days.find((d) => d.meals.dinner)?.meals
      .dinner;
    if (firstDinner) {
      setParams({ recipe: firstDinner.slug });
    } else {
      navigate("/menu");
    }
  }

  return (
    <div className="dark flex h-dvh flex-col bg-background text-foreground">
      {/* Celebration overlay — batch complete */}
      {showCelebration && <BatchCelebration />}

      {/* App bar */}
      <header className="relative z-30 flex items-center justify-between px-5 pt-6">
        <Link
          to="/menu"
          aria-label="Quitter le mode cuisine"
          className="flex h-9 w-9 items-center justify-center text-foreground hover:text-accent-dim"
        >
          <X className="size-5" />
        </Link>
        <div className="text-[11px] uppercase tracking-[0.22em] text-foreground/60">
          Étape {session.index + 1} / {total}
        </div>
        <button
          aria-label="Recommencer"
          onClick={session.reset}
          className="flex h-9 w-9 items-center justify-center text-foreground/70 hover:text-foreground"
        >
          <RotateCcw className="size-[18px]" strokeWidth={1.25} />
        </button>
      </header>

      {/* Mode toggle + context */}
      <div className="relative z-30 mt-3 flex items-center justify-between gap-3 px-5">
        <ModeToggle mode={mode} onChange={switchMode} />
        <div className="min-w-0 text-right">
          {mode === "batch" ? (
            <div className="font-mono text-[11px] text-foreground/50">
              {total} étapes
            </div>
          ) : (
            <div className="truncate font-serif text-[13px] italic text-foreground/70">
              {session.title}
            </div>
          )}
        </div>
      </div>

      {/* Progress */}
      <div className="relative z-30 px-5 pt-3">
        <div className="h-[2px] overflow-hidden rounded-full bg-foreground/10">
          <div
            className="h-full bg-accent-dim transition-[width] duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Story body */}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        <TapZones
          onPrev={handlePrev}
          onNext={handleNext}
          onCenter={handleTimerToggle}
        />
        <div className="pointer-events-none relative flex h-full flex-col">
          {step.timer_seconds != null && timer.remaining != null && (
            <div className="px-5 pt-6 text-center">
              <Eyebrow accent className="mb-3 justify-center">
                {timer.ringing
                  ? "Minuteur terminé"
                  : timer.running
                    ? "Minuteur en cours"
                    : "Minuteur prêt"}
              </Eyebrow>
              <div
                className={cn(
                  "font-serif text-[min(6rem,14vh)] font-light leading-[0.9] tracking-[-0.04em] tabular-nums text-foreground transition-colors",
                  timer.ringing && "text-accent-dim",
                )}
              >
                {formatTimer(timer.remaining)}
              </div>
              <div className="mt-2 text-[12px] tracking-wider text-foreground/55">
                {step.quantity ?? `/ ${formatTimer(step.timer_seconds)}`}
              </div>
            </div>
          )}

          <div className="flex-1 px-7 pt-5">
            <Eyebrow accent className="mb-4">
              Étape {step.order}
            </Eyebrow>
            <CookingStepIcon assetKey={step.asset_key} />
            <div className="font-serif text-[min(4rem,10vh)] font-light leading-[0.95] tracking-[-0.04em] text-foreground">
              {step.verb}
            </div>
            {step.quantity && (
              <div className="mt-6 inline-block bg-foreground px-4 py-2 font-mono text-[14px] text-background">
                {step.quantity}
              </div>
            )}
            <p className="mt-6 font-serif text-[22px] font-light italic leading-snug text-foreground/80">
              {step.target}.
            </p>

            {step.detail && (
              <p className="mt-4 text-[15px] leading-relaxed text-foreground/60">
                {step.detail}
              </p>
            )}

            {step.parallel_with.length > 0 && (
              <ParallelHint
                indices={step.parallel_with}
                steps={session.steps}
                onJump={(order) => {
                  const idx = session.steps.findIndex((s) => s.order === order);
                  if (idx >= 0) {
                    haptics.tick();
                    session.goTo(idx);
                  }
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Bottom controls + legal disclaimer */}
      <div className="relative z-30 flex flex-col border-t border-foreground/10 px-5 pt-4">
        <div className="flex gap-2.5">
          {step.timer_seconds != null ? (
            <Button
              variant="outline"
              size="icon-lg"
              onClick={handleTimerToggle}
              className="border-foreground/20 text-foreground hover:bg-foreground/10 hover:text-foreground"
              aria-label={timer.running ? "Pause" : "Lancer le minuteur"}
            >
              {timer.running ? (
                <Pause className="size-4" />
              ) : (
                <Play className="size-4" />
              )}
            </Button>
          ) : (
            <Button
              variant="outline"
              size="icon-lg"
              onClick={handlePrev}
              disabled={session.index === 0}
              className="border-foreground/20 text-foreground hover:bg-foreground/10 hover:text-foreground"
              aria-label="Étape précédente"
            >
              ←
            </Button>
          )}
          <Button
            size="lg"
            variant="cream"
            className="flex-1"
            onClick={handleComplete}
          >
            {isDone ? "Annuler" : "Marquer terminé"} <Check />
          </Button>
          <Button
            variant="outline"
            size="icon-lg"
            onClick={handleNext}
            disabled={session.index >= total - 1}
            className="border-foreground/20 text-foreground hover:bg-foreground/10 hover:text-foreground"
            aria-label="Étape suivante"
          >
            →
          </Button>
        </div>
        <p className="py-2.5 text-center text-[10px] leading-tight text-foreground/25">
          Les conseils sont générés par IA — restez vigilant·e sur la
          conservation et les allergies.
        </p>
      </div>

      {/* Phase 11 — AI Chat FAB */}
      {session.recipe && (
        <div className="fixed bottom-32 right-5 z-50">
          <Button
            size="icon-lg"
            variant="cream"
            className="h-14 w-14 rounded-full shadow-lg"
            onClick={() => setChatOpen(true)}
            aria-label="Assistant Cuisine"
          >
            <MessageCircle className="size-6" />
          </Button>
        </div>
      )}

      {/* Chat sheet */}
      {session.recipe && (
        <CookingChatSheet
          open={chatOpen}
          onOpenChange={setChatOpen}
          recipe={session.recipe}
          currentStep={step}
        />
      )}
    </div>
  );
}

// ─── Sub-components

function ModeToggle({
  mode,
  onChange,
}: {
  mode: CookingMode;
  onChange: (next: CookingMode) => void;
}) {
  // Phase 10 — segmented control switching between Batch and per-recipe.
  return (
    <div
      role="tablist"
      aria-label="Mode de cuisine"
      className="inline-flex items-center rounded-pill border border-foreground/20 bg-foreground/5 p-0.5"
    >
      {(
        [
          { id: "batch", label: "Tout le batch", Icon: ChefHat },
          { id: "recipe", label: "Une recette", Icon: NotebookText },
        ] as const
      ).map(({ id, label, Icon }) => {
        const active = mode === id;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(id)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-pill px-3 py-1 text-[11px] tracking-[0.05em] transition-colors",
              active
                ? "bg-background text-foreground"
                : "text-foreground/60 hover:text-foreground",
            )}
          >
            <Icon className="size-3" strokeWidth={1.5} />
            {label}
          </button>
        );
      })}
    </div>
  );
}

function LoadingScreen({ title, sub }: { title?: string; sub?: string } = {}) {
  return (
    <div className="grid h-full place-items-center bg-foreground text-background">
      <div className="px-6 text-center">
        {title && <p className="font-serif text-[18px] italic">{title}</p>}
        <p className="mt-2 text-[13px] text-background/60">
          {sub ?? "Chargement…"}
        </p>
      </div>
    </div>
  );
}

function FallbackScreen({ title, body }: { title: string; body?: string }) {
  return (
    <div className="grid h-full place-items-center bg-foreground text-background">
      <div className="max-w-sm px-6 text-center">
        <p className="font-serif text-[18px] italic">{title}</p>
        {body && <p className="mt-2 text-[13px] text-background/60">{body}</p>}
        <Button asChild variant="cream" size="default" className="mt-6">
          <Link to="/menu">Retour au menu</Link>
        </Button>
      </div>
    </div>
  );
}

// ─── Tap zones (BRD §3.4: one-tap navigation) ────────────────────────
function TapZones({
  onPrev,
  onNext,
  onCenter,
}: {
  onPrev: () => void;
  onNext: () => void;
  onCenter: () => void;
}) {
  return (
    <div
      className="pointer-events-auto absolute inset-0 z-10 grid grid-cols-3"
      aria-hidden
    >
      <button
        onClick={onPrev}
        aria-label="Étape précédente"
        className="h-full focus:outline-none focus-visible:bg-foreground/[0.03]"
      />
      <button
        onClick={onCenter}
        aria-label="Lancer ou mettre en pause le minuteur"
        className="h-full focus:outline-none focus-visible:bg-foreground/[0.03]"
      />
      <button
        onClick={onNext}
        aria-label="Étape suivante"
        className="h-full focus:outline-none focus-visible:bg-foreground/[0.03]"
      />
    </div>
  );
}

// ─── Parallel hint ────────────────────────────────────────────────────
function ParallelHint({
  indices,
  steps,
  onJump,
}: {
  indices: number[];
  steps: CookingStep[];
  onJump: (order: number) => void;
}) {
  const matches = indices
    .map((order) => steps.find((s) => s.order === order))
    .filter((s): s is CookingStep => Boolean(s));
  if (matches.length === 0) return null;
  return (
    <div className="pointer-events-auto relative z-20 mt-10 border border-foreground/10 bg-foreground/5 p-4">
      <Eyebrow className="mb-2 text-foreground/55">Pendant ce temps</Eyebrow>
      <ul className="space-y-1.5">
        {matches.map((m) => (
          <li key={m.order}>
            <button
              onClick={() => onJump(m.order)}
              className="text-left font-serif text-[15px] italic text-foreground/80 hover:text-foreground"
            >
              {m.verb.toLowerCase()} {m.target}
              {m.quantity ? ` — ${m.quantity}` : ""}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Cooking step icon ───────────────────────────────────────────────

const ICON_MAP: Record<
  CookingIconName,
  React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
  chop: ChopIcon,
  stir: StirIcon,
  boil: BoilIcon,
  roast: RoastIcon,
  peel: PeelIcon,
  season: SeasonIcon,
  rest: RestIcon,
  oven: OvenIcon,
  timer: TimerIcon,
};

function CookingStepIcon({ assetKey }: { assetKey: string | null }) {
  const name = resolveAssetKey(assetKey);
  if (!name) return null;

  const Icon = ICON_MAP[name];
  return (
    <div className="mb-5 flex items-center justify-start">
      <Icon className="size-10 text-foreground/60" aria-hidden />
    </div>
  );
}

// ─── Batch celebration overlay ────────────────────────────────────────

function BatchCelebration() {
  const navigate = useNavigate();
  const animFrameRef = useRef<number>(0);

  const fireFireworks = useCallback(() => {
    const duration = 5000;
    const end = Date.now() + duration;
    const colors = ["#ffd700", "#ff69b4", "#00e5ff", "#76ff03", "#ff6d00"];

    function frame() {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        startVelocity: 60,
        origin: { x: 0, y: 0.65 },
        colors,
        zIndex: 9999,
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        startVelocity: 60,
        origin: { x: 1, y: 0.65 },
        colors,
        zIndex: 9999,
      });
      if (Date.now() < end) {
        animFrameRef.current = requestAnimationFrame(frame);
      }
    }

    animFrameRef.current = requestAnimationFrame(frame);
  }, []);

  useEffect(() => {
    fireFireworks();
    return () => {
      cancelAnimationFrame(animFrameRef.current);
      confetti.reset();
    };
  }, [fireFireworks]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-background px-8 text-center">
      <div className="mb-6 text-[80px] leading-none" aria-hidden>
        🎉
      </div>
      <h1 className="font-serif text-[38px] italic leading-[1.1] text-foreground">
        Batch terminé !
      </h1>
      <p className="mt-4 text-[15px] leading-relaxed text-foreground/60">
        Bravo, tout est prêt pour la semaine.
        <br />
        Profitez de vos repas&nbsp;!
      </p>
      <button
        onClick={() => navigate("/dashboard")}
        className="mt-10 flex w-full max-w-xs items-center justify-center bg-foreground py-4 font-serif text-[17px] italic text-background transition-opacity active:opacity-75"
      >
        Retour au tableau de bord
      </button>
      <button
        onClick={() => navigate("/menu")}
        className="mt-4 text-[13px] text-foreground/40 hover:text-foreground/70"
      >
        Voir le menu →
      </button>
    </div>
  );
}
